import { scaleLinear } from 'd3-scale';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { useWebsocketStore } from '../../../../../stores/websocketStore/websocketProvider';
import { AskLine } from './AskLine';
import { BidLine } from './BidLine';
import { LastLine } from './LastLine';
import style from './BidAskLineChart.module.css';
import BidAskVerticalLabels from './BidAskVerticalLabels';
import BidAskVerticalTicks from './BidAskVerticalTicks';
import { max, min } from 'd3-array';
import { MouseCoordinatesWrapper } from '../candlestickchart/newCandleChart/MouseCoordinatesWrapper';
import ChartCrossHairs from '../candlestickchart/newCandleChart/ChartCrossHairs';
import { Body3 } from '../../../../atomic/text/Text';
import { colors } from '../../../../../colorVars';
import { OrdersContainer } from '../candlestickchart/newCandleChart/orders/OrdersContainer';
import SoftOrdersContainer from '../candlestickchart/newCandleChart/softOrders/SoftOrdersContainer';
import { useContextMenu } from 'react-contexify';
import { BidAskContextMenu } from './bidAskContextMenu/BidAskContextMenu';
import {
  buildMarketDataKeys,
} from '../../../../../stores/websocketStore/utils/marketDataRouting';
import { resolveMarketDataBrokerage } from '../../../../../utils/accountRouting';
import { isOptionInstrument } from '../../../tradeplans/activeTradePlanModule/utils/optionInstrument';
import { resolveBidAskChartSymbol } from './bidAskTradePlanSymbol';

const maxDataPoints = 50;

export const BidAskLineChart = observer(({ tradePlanId, size }) => {
  const { priceDataStore, tradePlanStore, accountBalanceStore } = useWebsocketStore();
  const tradePlan = tradePlanStore.tradePlans[tradePlanId] ?? null;
  const instrument = tradePlan?.instrument ?? null;
  const isOptionTrade = isOptionInstrument(instrument);
  const symbol = resolveBidAskChartSymbol(tradePlan);
  const dataSource = tradePlan?.dataSource ?? null;
  const accounts =
    Object.keys(accountBalanceStore.balances).map((accountNumber) => {
      const balance = accountBalanceStore.balances[accountNumber];
      return {
        accountNumber,
        brokerage: balance?.brokerage,
      };
    }) ?? [];
  const brokerage = resolveMarketDataBrokerage({
    accountNumber: tradePlan?.accountNumber,
    accounts,
    currentBrokerage: tradePlan?.brokerage,
    dataSource,
    symbol,
  });

  const { show } = useContextMenu({ id: tradePlanId + 'bidAskChart' });
  const displayContextMenu = (e, y) => {
    // e.preventDefault();
    show(e, { props: { yCoordinate: y } });
  };

  const [dataArray, setDataArray] = useState([]);
  const [priceMaximum, setPriceMaximum] = useState(null);
  const [priceMinimum, setPriceMinimum] = useState(null);
  const [scaleX, setScaleX] = useState(null);
  const [scaleY, setScaleY] = useState(null);

  const chartRef = useRef(null);
  const portalRef = useRef();

  const width = size - 35;
  // const [showCrossHairs, setShowCrossHairs] = useState();

  const hash = buildMarketDataKeys({ symbol, dataSource, brokerage }).find(
    (key) => priceDataStore.lastQuote[key] || priceDataStore.trades[key]
  );
  const bid = (hash && priceDataStore.lastQuote[hash]?.bid) ?? null;
  const ask = (hash && priceDataStore.lastQuote[hash]?.ask) ?? null;
  const last = (hash && priceDataStore.trades[hash]?.price) ?? null;
  useEffect(() => {
    if (bid !== null && ask !== null && last !== null) {
      if (dataArray.length > 1) {
        setPriceMinimum(min(dataArray, (c) => Number(c.bid)));
        setPriceMaximum(max(dataArray, (c) => Number(c.ask)));
      }

      const dataArrayCopy = [...dataArray];
      dataArrayCopy.push({
        bid: Number(bid),
        ask: Number(ask),
        last: Number(last),
      });
      if (dataArrayCopy.length > maxDataPoints + 1) dataArrayCopy.shift();
      setDataArray(dataArrayCopy);
    }
  }, [bid, ask, last]);

  useEffect(() => {
    if (
      priceMinimum !== null &&
      priceMaximum !== null &&
      bid !== null &&
      ask !== null &&
      last !== null
    ) {
      let divisor = 300;
      if (isOptionTrade) divisor = 10;
      const average = ((priceMaximum + priceMinimum) / 2).toFixed(0);
      const xScale = scaleLinear()
        .domain([0, maxDataPoints])
        .range([0, width])
        .nice();
      setScaleX(() => xScale);
      const yScale = scaleLinear()
        .domain([priceMinimum - average / divisor, priceMaximum + average / divisor])
        .range([size, 0]);
      setScaleY(() => yScale);
    }
  }, [priceMinimum, priceMaximum]);
  if (isOptionTrade && tradePlan.inPosition === false)
    return (
      <div
        className={style.container}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50px',
          width: '100%',
        }}
      >
        <Body3 fontWeight={700} color={colors.lightGrey}>
          Waiting for entry.
        </Body3>
      </div>
    );
  return (
    <div className={style.container}>
      <MouseCoordinatesWrapper displayContextMenu={displayContextMenu} svgRef={chartRef}>
        <svg width={width} height={size} ref={chartRef}>
          {scaleY !== null && dataArray.length > 1 ? (
            <>
              <g>
                <BidLine
                  dataArray={dataArray}
                  scaleX={scaleX}
                  scaleY={scaleY}
                />
                <AskLine
                  dataArray={dataArray}
                  scaleX={scaleX}
                  scaleY={scaleY}
                />
                <LastLine
                  dataArray={dataArray}
                  scaleX={scaleX}
                  scaleY={scaleY}
                />
              </g>
              <OrdersContainer
                symbol={symbol}
                tradePlanId={tradePlanId}
                size={width}
                scaleY={scaleY}
                portalRef={portalRef}
              />
              <SoftOrdersContainer
                chartType="bidAsk"
                tradePlanId={tradePlanId}
                symbol={symbol}
                size={size}
                scaleY={scaleY}
                portalRef={portalRef}
              />
              <BidAskVerticalTicks
                width={width}
                size={size}
                scaleY={scaleY}
                numberOfTicks={10}
                tickSize={5}
                weight={1}
              />
            </>
          ) : null}
          <ChartCrossHairs size={size} bottomPadding={0} />
          <g ref={portalRef}></g>
          <line stroke={colors.lightGrey} x1={0} x2={width} y1={size} y2={size}/>
        </svg>
        <BidAskVerticalLabels
          numberOfTicks={10}
          scaleY={scaleY}
          size={size}
          fontSize="0.5rem"
        />
        <BidAskContextMenu scaleY={scaleY} tradePlanId={tradePlanId}/>
      </MouseCoordinatesWrapper>
    </div>
  );
});
